import { collection, getDocs, doc, updateDoc } from 'firebase/firestore';
import { db } from '../config/firebase';

/**
 * Recalculate and fix savings for all packages in Firestore
 * This script:
 * 1. Fetches all packages and services from Firestore
 * 2. For each package, calculates correct savings = sum(service prices) - package price
 * 3. Updates the package with the correct savings value
 */
export async function fixAllPackageSavings() {
  try {
    console.log('🔧 Starting to fix package savings...');
    
    // Fetch all services
    const servicesSnapshot = await getDocs(collection(db, 'services'));
    const servicesMap = new Map();
    servicesSnapshot.docs.forEach(doc => {
      const data = doc.data();
      servicesMap.set(doc.id, {
        id: doc.id,
        name: data.name,
        price: Number(data.price || 0) // Ensure numeric
      });
    });
    
    console.log(`📦 Found ${servicesMap.size} services`);
    
    // Fetch all packages
    const packagesSnapshot = await getDocs(collection(db, 'packages'));
    console.log(`📦 Found ${packagesSnapshot.docs.length} packages to fix`);
    
    let updated = 0;
    let errors = 0;
    
    for (const packageDoc of packagesSnapshot.docs) {
      try {
        const packageData = packageDoc.data();
        const packageId = packageDoc.id;
        const packageName = packageData.name;
        const packagePrice = Number(packageData.price || 0);
        const originalPrice = Number(packageData.originalPrice || 0);
        const serviceIds = packageData.serviceIds || [];
        
        let correctSavings = 0;
        let calculationMethod = '';
        
        // Method 1: If originalPrice exists and is greater than price, use that
        if (originalPrice > packagePrice) {
          correctSavings = originalPrice - packagePrice;
          calculationMethod = 'originalPrice - price';
        } else {
          // Method 2: Calculate from service prices
          let totalServicePrice = 0;
          const missingServices: string[] = [];
          
          for (const serviceId of serviceIds) {
            const service = servicesMap.get(serviceId);
            if (service) {
              totalServicePrice += Number(service.price);
            } else {
              missingServices.push(serviceId);
            }
          }
          
          correctSavings = totalServicePrice - packagePrice;
          calculationMethod = `service sum (${serviceIds.length} services, ${missingServices.length} missing) - price`;
          
          if (missingServices.length > 0) {
            console.log(`   ⚠️  Warning for "${packageName}": ${missingServices.length} service(s) not found`);
          }
        }
        
        const oldSavings = Number(packageData.savings || 0);
        
        // FORCE UPDATE: Always update to ensure consistency
        await updateDoc(doc(db, 'packages', packageId), {
          savings: correctSavings,
          updatedAt: new Date().toISOString()
        });
        
        console.log(`✅ Updated "${packageName}"`);
        console.log(`   Old savings: ₹${oldSavings}`);
        console.log(`   New savings: ₹${correctSavings}`);
        console.log(`   Calculation: ${calculationMethod}`);
        console.log(`   Price: ₹${packagePrice}, Original: ₹${originalPrice}`);
        
        updated++;
      } catch (error) {
        console.error(`❌ Error updating package ${packageDoc.id}:`, error);
        errors++;
      }
    }
    
    console.log(`\n✨ Complete! Updated ${updated} packages, ${errors} errors`);
    return { success: true, updated, errors };
  } catch (error) {
    console.error('❌ Error fixing package savings:', error);
    return { success: false, error };
  }
}
